// src/components/BulkRiskUploadModal.jsx
import React, { useState } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { bulkUploadRisks } from "../../api/riskApi"; 


const categories = ["Operational", "Compliance", "Financial", "Technical", "Other"];
const likelihoods = ["Low", "Medium", "High"];
const impacts = ["Low", "Medium", "High"];
const statuses = ["Open", "In Progress", "Resolved", "Closed"];
const ratings = ["Low", "Medium", "High"]; 

const initialRisk = {
  title: "",
  description: "",
  category: "",
  likelihood: "",
  impact: "",
  status: "Open",
  mitigation: "",
  contingencyPlan: "",
  riskOwner: "",
  causes: "",
  consequences: "",
  rating: "",
  comments: "",
  treatmentActions: [],
};

const toCamelCase = (str) => {
  return str
    .replace(/[^a-zA-Z0-9]+(.)?/g, (match, chr) =>
      chr ? chr.toUpperCase() : ""
    )
    .replace(/^./, (str) => str.toLowerCase());
};

export default function BulkRiskUploadModal({ closeModal, onRisksUploaded }) {
  const [risks, setRisks] = useState([initialRisk]);
  const [loading, setLoading] = useState(false);

  const processImportedData = (data) => {
    if (!data || data.length === 0) {
      setRisks([initialRisk]);
      toast.error("The file is empty or has no data.");
      return;
    }

    const newRisks = data.map((item) => {
      let riskObject = { ...initialRisk };
      for (const key in item) {
        if (Object.prototype.hasOwnProperty.call(item, key)) {
          const normalizedKey = toCamelCase(key.trim());
          if (riskObject.hasOwnProperty(normalizedKey)) {
            riskObject[normalizedKey] = item[key];
          }
        }
      }
      return riskObject;
    });

    const filteredRisks = newRisks.filter((r) => r.title);
    if (filteredRisks.length > 0) {
      setRisks(filteredRisks);
      toast.success("File imported successfully. Review before saving.");
    } else {
      setRisks([initialRisk]);
      toast.error("The file contains no valid risk data. Please check the 'Title' column.");
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const ext = file.name.split(".").pop().toLowerCase();
      if (ext === "csv") {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => processImportedData(results.data),
          error: () => toast.error("Failed to parse CSV file."),
        });
      } else if (ext === "xlsx") {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const data = new Uint8Array(event.target.result);
            const workbook = XLSX.read(data, { type: "array" });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
            processImportedData(jsonData);
          } catch {
            toast.error("Failed to parse Excel file.");
          }
        };
        reader.readAsArrayBuffer(file);
      } else {
        toast.error("Unsupported file format. Upload .csv or .xlsx.");
      }
    }
  };

  const handleDownloadTemplate = () => {
    const headers = [
      "Title",
      "Description",
      "Category",
      "Likelihood",
      "Impact",
      "Status",
      "Mitigation",
      "Contingency Plan",
      "Risk Owner",
      "Causes",
      "Consequences",
      "Rating",
      "Comments",
      "Treatment Actions"
    ];
    const csvContent = headers.join(",");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "risk_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const risksToSubmit = risks.filter((r) => r.title);
    if (risksToSubmit.length === 0) {
      return toast.error("No valid risks to add. Please fill in at least the title.");
    }

    setLoading(true);
    try {
      await bulkUploadRisks(risksToSubmit);
      toast.success(`${risksToSubmit.length} risk(s) added successfully! 🎉`);
      onRisksUploaded();
      closeModal();
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || "Unexpected error.";
      toast.error(`Error: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const renderInput = (field, value, onChange) => {
    if (field === "category") {
      return (
        <select value={value} onChange={onChange} className="p-2 border rounded w-full text-sm">
          <option value="">-- Select --</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      );
    }
    if (field === "likelihood") {
      return (
        <select value={value} onChange={onChange} className="p-2 border rounded w-full text-sm">
          <option value="">-- Select --</option>
          {likelihoods.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
      );
    }
    if (field === "impact") {
      return (
        <select value={value} onChange={onChange} className="p-2 border rounded w-full text-sm">
          <option value="">-- Select --</option>
          {impacts.map((i) => <option key={i} value={i}>{i}</option>)}
        </select>
      );
    }
    if (field === "status") {
      return (
        <select value={value} onChange={onChange} className="p-2 border rounded w-full text-sm">
          <option value="">-- Select --</option>
          {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      );
    }
    if (field === "rating") {
    return (
      <select value={value} onChange={onChange} className="p-2 border rounded w-full text-sm">
        <option value="">-- Select --</option>
        {ratings.map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
    );
  }
    return (
      <input
        type="text"
        value={value}
        onChange={onChange}
        className="p-2 border rounded w-full text-sm"
      />
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 overflow-y-auto">
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-xl shadow-xl p-6 w-full max-w-6xl relative space-y-6 max-h-[90vh] overflow-y-auto"
      >
        <button
          type="button"
          onClick={closeModal}
          className="absolute top-2 right-2 text-gray-500 hover:text-red-500 text-xl font-bold"
        >
          &times;
        </button>
        <h2 className="text-2xl font-semibold text-orange-600">Bulk Upload Risks</h2>

        {/* Upload + Download Template */}
        <div className="flex justify-between items-center border-b pb-4">
          <input type="file" accept=".csv, .xlsx" onChange={handleFileUpload} className="block w-full text-sm text-gray-500" />
          <button type="button" onClick={handleDownloadTemplate} className="ml-4 text-orange-500 hover:underline">
            Download Template
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto border rounded">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {[
                  "Title",
                  "Description",
                  "Category",
                  "Likelihood",
                  "Impact",
                  "Status",
                  "Mitigation",
                  "Contingency Plan",
                  "Risk Owner",
                  "Causes",
                  "Consequences",
                  "Rating",
                  "Comments",
                  "Treatment Actions"
                ].map((h, i) => (
                  <th key={i} className="p-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {risks.map((risk, idx) => (
                <tr key={idx} className="border-b hover:bg-gray-50">
                  {Object.keys(initialRisk).map((field, i) => (
                    <td key={i} className="p-2">
                      {renderInput(field, risk[field], (e) => {
                        const newRisks = [...risks];
                        newRisks[idx] = { ...newRisks[idx], [field]: e.target.value };
                        setRisks(newRisks);
                      })}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <button type="button" onClick={closeModal} className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-100">
            Cancel
          </button>
          <button type="submit" disabled={loading} className="px-4 py-2 rounded bg-orange-600 text-white hover:bg-orange-700">
            {loading ? "Saving..." : "Save All Risks"}
          </button>
        </div>
      </form>
    </div>
  );
}

